import { CATALOG_FIELD_REGISTRY, catalogColumnId, type CatalogFieldGate } from '@ooh/contracts';

export type OfferMediaUnitSurface = 'unified' | 'decisions';

const ALL_OFFER_SURFACES = ['unified', 'decisions'] as const;

/**
 * The only offer-specific field list. Field identity and presentation metadata
 * are resolved from CATALOG_FIELD_REGISTRY by getOfferMediaUnitFields().
 *
 * The `units` and `pricing` surface keys have merged into one `unified` key
 * (`offer-unified-table` capability, CU-86cbc3r8b): the Nośniki and Wyceny
 * tabs fold into one merged table, so the `unified` column is the union of
 * the two former columns — every field that applied to either former surface
 * stays applicable, none is lost.
 */
export const OFFER_MEDIA_UNIT_FIELD_APPLICABILITY = {
  mediaType: ALL_OFFER_SURFACES,
  formatCode: ALL_OFFER_SURFACES,
  supplierId: ALL_OFFER_SURFACES,
  supplierNumber: ALL_OFFER_SURFACES,
  grwId: ['decisions'],
  region: ALL_OFFER_SURFACES,
  county: ALL_OFFER_SURFACES,
  agglomeration: ALL_OFFER_SURFACES,
  city: ALL_OFFER_SURFACES,
  address: ALL_OFFER_SURFACES,
  width: ALL_OFFER_SURFACES,
  height: ALL_OFFER_SURFACES,
  audience: ALL_OFFER_SURFACES,
  notes: ALL_OFFER_SURFACES,
  // Merged table only — unlike `notes`, NOT ['decisions'] too:
  // `decision-shared-fields.ts` builds its columns against
  // `UnitDecisionsListItem` (@ooh/contracts), a separate schema that has no
  // wfContactPerson/locationDescription fields. Including 'decisions' here
  // would silently render two always-blank columns in the client-decisions
  // tab, which is out of scope for this fix.
  wfContactPerson: ['unified'],
  locationDescription: ['unified'],
  noPhoto: ALL_OFFER_SURFACES,
  lighting: ['unified'],
  material: ['unified'],
  multiformat: ['unified'],
  tags: ['unified'],
  roadNumber: ['unified'],
  area: ['unified'],
} as const satisfies Record<string, readonly OfferMediaUnitSurface[]>;

export type OfferMediaUnitFieldId = keyof typeof OFFER_MEDIA_UNIT_FIELD_APPLICABILITY;

export interface OfferMediaUnitField {
  id: OfferMediaUnitFieldId;
  columnId: string;
  sortable: boolean;
  gate: CatalogFieldGate;
}

const registryById = new Map(CATALOG_FIELD_REGISTRY.map((entry) => [entry.id, entry]));

/**
 * Metadata for offer-only fields that intentionally have NO `CATALOG_FIELD_REGISTRY`
 * entry. `wfContactPerson`/`locationDescription` are catalog columns without a
 * catalog filter (`filter-fields-catalog-only.ts`'s `CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS`
 * omits them by design) — registering them in `CATALOG_FIELD_REGISTRY`, which
 * enforces strict column<->filter parity against the REAL rendered catalog UI
 * (`catalog-column-filter-parity`), would force the catalog to gain filters it
 * doesn't have. Offer tabs only need column/sort/gate metadata, not
 * registry-driven filter parity, so they get this small local fallback instead.
 */
const OFFER_ONLY_FIELD_METADATA: Readonly<
  Record<string, { columnId: string; sortable: boolean; gate: CatalogFieldGate }>
> = {
  wfContactPerson: { columnId: 'wfContactPerson', sortable: true, gate: 'none' },
  locationDescription: { columnId: 'locationDescription', sortable: true, gate: 'none' },
};

/**
 * Server sort-key aliases for registry columnIds whose column id does not match
 * the query schema's sortBy value. Shared by units/pricing column builders and
 * by their registry-parity tests so the alias is defined exactly once.
 */
export const OFFER_MEDIA_UNIT_SORT_KEY_ALIASES: Readonly<Record<string, string>> = {
  audienceEstimate: 'audience',
};

export function getOfferMediaUnitFields(surface: OfferMediaUnitSurface): OfferMediaUnitField[] {
  return (Object.keys(OFFER_MEDIA_UNIT_FIELD_APPLICABILITY) as OfferMediaUnitFieldId[]).flatMap(
    (id) => {
      const surfaces = OFFER_MEDIA_UNIT_FIELD_APPLICABILITY[id] as readonly OfferMediaUnitSurface[];
      if (!surfaces.includes(surface)) return [];

      const entry = registryById.get(id);
      if (entry) {
        return [{ id, columnId: catalogColumnId(entry), sortable: entry.sort, gate: entry.gate }];
      }

      const offerOnly = OFFER_ONLY_FIELD_METADATA[id];
      if (offerOnly) {
        return [
          { id, columnId: offerOnly.columnId, sortable: offerOnly.sortable, gate: offerOnly.gate },
        ];
      }

      throw new Error(`Catalog field registry entry missing for ${id}`);
    },
  );
}
